import { esc } from "./watchlistData";
import type { OptionMoneyness } from "./watchlistData";

/**
 * Early-BTC flag for short wheel options.
 *
 * Market practice (Tastytrade / short-premium research) is: sell ~45 DTE, then
 * close at 50% of credit OR ~21 DTE remaining, whichever first. 21/45 ≈ half
 * the original life. That 21-DTE number is not a universal floor — it is a
 * fraction of a 45-DTE entry. Applying 14 DTE remaining to a 7–14 DTE book
 * never fires.
 *
 * Generalization used here (works for weeklies and monthlies):
 *   idleScore = capturedFrac × remainingFrac
 * High score = profit already booked AND a large share of the original life
 * still left (capital sitting on leftover theta). Threshold 0.22 ≈ Tasty's
 * 50% captured with ~44% of life remaining (50% × 21/45).
 *
 * Hard floors: captured ≥ 50%, ≥ 3 DTE left (do not nag into last-day gamma),
 * OTM, tradable mark. When open/expiry dates are missing, fall back to leftover
 * annualized ROC vs a ~2%/month hurdle.
 */
export const BTC_EARLY_CLOSE_MIN_CAPTURED_PCT = 50;
export const BTC_EARLY_CLOSE_MIN_DTE = 3;
export const BTC_EARLY_CLOSE_MIN_MARK = 0.05;
export const BTC_EARLY_CLOSE_IDLE_SCORE = 0.22;
export const BTC_EARLY_CLOSE_LEFTOVER_HURDLE_ANN = 24;

export interface BtcEarlyCloseInput {
  capturedPct: number | null | undefined;
  dte: number | null | undefined;
  mark: number | null | undefined;
  moneyness: OptionMoneyness;
  stockPrice: number | null | undefined;
  strike: number | null | undefined;
  contracts: number | null | undefined;
  avgCost: number | null | undefined;
  isCall: boolean;
  expired?: boolean;
  isShort?: boolean;
  openDate?: string | null;
  expDate?: string | null;
}

export interface BtcEarlyCloseSignal {
  flag: boolean;
  tip: string;
  leftoverAnnRoc: number | null;
  idleScore: number | null;
  originalDte: number | null;
}

function finite(value: number | null | undefined): number | null {
  return value != null && Number.isFinite(value) ? value : null;
}

function utcDayMs(raw: string | null | undefined): number | null {
  const m = String(raw ?? "")
    .slice(0, 10)
    .match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function calendarDays(from: string | null | undefined, to: string | null | undefined): number | null {
  const a = utcDayMs(from);
  const b = utcDayMs(to);
  if (a == null || b == null) return null;
  return Math.round((b - a) / 86_400_000);
}

function formatLeftoverAnn(pct: number): string {
  if (Math.abs(pct) < 10) return pct.toFixed(1).replace(/\.0$/, "");
  return String(Math.round(pct));
}

export function resolveOriginalDte(input: BtcEarlyCloseInput): number | null {
  const span = calendarDays(input.openDate, input.expDate);
  if (span != null && span > 0) return span;
  return null;
}

/** Best-case leftover yield if the short expires worthless. */
export function leftoverAnnRocPct(input: BtcEarlyCloseInput): number | null {
  const mark = finite(input.mark);
  const dte = finite(input.dte);
  const contracts = Math.abs(finite(input.contracts) ?? 0);
  if (mark == null || mark < 0 || dte == null || dte <= 0 || contracts <= 0) return null;
  const avg = finite(input.avgCost);
  const capitalPerShare =
    input.isCall && avg != null && avg > 0 ? avg : finite(input.strike);
  if (capitalPerShare == null || capitalPerShare <= 0) return null;
  return (mark / capitalPerShare) * (365 / dte) * 100;
}

function buildTip(opts: {
  captured: number | null;
  dte: number | null;
  originalDte: number | null;
  leftover: number | null;
  isCall: boolean;
}): string {
  const parts: string[] = [];
  if (opts.captured != null) {
    if (opts.originalDte != null && opts.dte != null && opts.originalDte > 0) {
      const held = Math.max(0, opts.originalDte - opts.dte);
      parts.push(`Captured ${Math.round(opts.captured)}% in ${held} of ${opts.originalDte} days`);
    } else {
      parts.push(`Captured ${Math.round(opts.captured)}%`);
    }
  }
  if (opts.dte != null) {
    parts.push(
      opts.originalDte != null
        ? `${Math.round(opts.dte)}/${opts.originalDte} DTE left`
        : `${Math.round(opts.dte)} DTE left`,
    );
  }
  if (opts.leftover != null) {
    const basis = opts.isCall ? "share cost" : "collateral";
    parts.push(`leftover ~${formatLeftoverAnn(opts.leftover)}% ann on ${basis}`);
  }
  return parts.join(" · ");
}

export function evaluateBtcEarlyClose(input: BtcEarlyCloseInput): BtcEarlyCloseSignal {
  // current_profit_pct / P/L % is already percent (1 = 1%). Scaling (−1, 1)
  // as a fraction marked +0.97% as 97% captured and drew a false BTC dot.
  const captured = finite(input.capturedPct);
  const dte = finite(input.dte);
  const leftover = leftoverAnnRocPct(input);
  const originalDte = resolveOriginalDte(input);
  const remainingFrac =
    originalDte != null && originalDte > 0 && dte != null
      ? Math.min(1, Math.max(0, dte / originalDte))
      : null;
  const capturedFrac = captured != null ? captured / 100 : null;
  const idleScore =
    capturedFrac != null && remainingFrac != null ? capturedFrac * remainingFrac : null;

  const mark = finite(input.mark);
  const stock = finite(input.stockPrice);
  const baseGates =
    input.expired !== true &&
    input.isShort !== false &&
    captured != null &&
    captured >= BTC_EARLY_CLOSE_MIN_CAPTURED_PCT &&
    dte != null &&
    dte >= BTC_EARLY_CLOSE_MIN_DTE &&
    mark != null &&
    mark >= BTC_EARLY_CLOSE_MIN_MARK &&
    stock != null &&
    input.moneyness !== "itm" &&
    input.moneyness !== "atm";

  const timedFlag = idleScore != null && idleScore >= BTC_EARLY_CLOSE_IDLE_SCORE;
  const fallbackFlag =
    idleScore == null &&
    leftover != null &&
    leftover < BTC_EARLY_CLOSE_LEFTOVER_HURDLE_ANN;
  const flag = baseGates && (timedFlag || fallbackFlag);

  return {
    flag,
    tip: buildTip({
      captured,
      dte,
      originalDte,
      leftover,
      isCall: input.isCall,
    }),
    leftoverAnnRoc: leftover,
    idleScore,
    originalDte,
  };
}

export function renderBtcEarlyCloseDot(signal: BtcEarlyCloseSignal): string {
  if (!signal.flag || !signal.tip) return "";
  const tip = esc(signal.tip);
  return `<span class="sfe-btc-early-dot sfe-data-tip-host" data-tip="${tip}" title="${tip}" role="img" tabindex="0" aria-label="BTC candidate: ${tip}"></span>`;
}

export function renderBtcEarlyCloseDotFrom(input: BtcEarlyCloseInput): string {
  return renderBtcEarlyCloseDot(evaluateBtcEarlyClose(input));
}
